import React from "react";
import { Link } from "react-router-dom";
import { Sparkles } from "lucide-react";
import { OriginBadge } from "../../lib/origin.jsx";
import { StatusDot } from "../Primitives";
import { testid } from "../../lib/forecastApi";
import { cn } from "../../lib/utils";
import { useDrill } from "./ForecastLayout";

const TONE = { success: "text-success", warning: "text-warning", critical: "text-critical", info: "text-info" };

export const originOf = (line, what) => ({ origin: line.origin, source: `Forecast / Prediction Models · ${what || line.label}` });

export function Cue({ className }) {
  return (
    <span className={cn("inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-primary opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100 transition-opacity", className)}>
      <Sparkles size={10} />Explain
    </span>
  );
}

// Any clickable line (indicator, driver, row): opens the drill-down for its id.
export function LineButton({ line, className, children, as: Tag = "div" }) {
  const { open } = useDrill();
  return (
    <Tag role="button" tabIndex={0} data-testid={`fc-line-${testid(line.id)}`} onClick={() => open(line.id)}
         onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); open(line.id); } }}
         className={cn("group text-left w-full cursor-pointer transition-colors hover:bg-surface-2/70 focus-visible:outline focus-visible:outline-1 focus-visible:outline-primary", className)}>
      {children}
    </Tag>
  );
}

export function KpiCell({ line }) {
  return (
    <LineButton line={line} className="px-4 py-3.5 h-full bg-surface">
      <div className="kpi-label flex items-center gap-2 min-h-[16px]">
        {line.tone && <StatusDot status={line.tone} />}
        <span className="truncate">{line.label}</span>
        <OriginBadge {...originOf(line)} className="ml-auto" testid={`fc-origin-${testid(line.id)}`} />
      </div>
      <div className={cn("kpi-value text-2xl mt-1.5 truncate", TONE[line.tone])} data-testid={`fc-value-${testid(line.id)}`}>{line.display}</div>
      <div className="flex items-center justify-between gap-2 mt-1">
        <span className="text-[11px] text-muted truncate">{line.sub || "\u00a0"}</span><Cue className="shrink-0" />
      </div>
    </LineButton>
  );
}

const AUTO_COLS = { 1: "md:grid-cols-1", 2: "md:grid-cols-2", 3: "md:grid-cols-3", 4: "md:grid-cols-4", 6: "md:grid-cols-3", 9: "md:grid-cols-3" };

export function KpiStrip({ kpis, cols }) {
  const n = kpis.length;
  return (
    <div className="tile overflow-hidden" data-testid="fc-kpis">
      <div className={cn("grid gap-px bg-border grid-cols-1", n % 2 === 0 && "sm:grid-cols-2", cols || AUTO_COLS[n] || "md:grid-cols-4")}>
        {kpis.map(k => <KpiCell key={k.id} line={k} />)}
      </div>
    </div>
  );
}

function EffectBar({ v }) {
  const w = Math.min(50, Math.abs(v || 0) / 2);
  return (
    <div className="relative h-1.5 w-24 sm:w-32 bg-surface-2 rounded-full shrink-0" aria-hidden>
      <span className="absolute top-0 bottom-0 left-1/2 w-px bg-border-strong" />
      <span className={cn("absolute top-0 bottom-0 rounded-full", v >= 0 ? "left-1/2 bg-primary" : "right-1/2 bg-warning")} style={{ width: `${w}%` }} />
    </div>
  );
}

export function DriverRow({ line, centered }) {
  return (
    <LineButton line={line} className="px-4 py-3 border-b border-border last:border-b-0">
      <div className="flex items-center gap-3">
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium flex items-center gap-2"><span className="truncate">{line.label}</span><Cue /></div>
          <div className="text-[11px] text-muted truncate">{line.sub}</div>
        </div>
        {centered ? <EffectBar v={line.effect} /> : <div className="h-1.5 w-24 sm:w-32 bg-surface-2 rounded-full shrink-0" aria-hidden>
          <div className="h-full bg-info rounded-full" style={{ width: `${Math.min(100, Math.abs(line.effect || 0))}%` }} /></div>}
        <span className="font-mono-tabular font-semibold text-sm w-16 text-right shrink-0" data-testid={`fc-value-${testid(line.id)}`}>{line.display}</span>
      </div>
    </LineButton>
  );
}

export function DriverPanel({ title, hint, drivers, centered, testid: tid }) {
  const origins = [...new Set(drivers.map(d => d.origin))];
  return (
    <div className="tile overflow-hidden" data-testid={tid}>
      <div className="flex items-center justify-between px-4 py-3 border-b border-border gap-2">
        <div className="min-w-0">
          <div className="text-[11px] font-semibold tracking-widest uppercase text-muted">{title}</div>
          {hint && <div className="text-[11px] text-subtle mt-0.5">{hint}</div>}
        </div>
        <OriginBadge origin={origins.length === 1 ? origins[0] : "mixed"} source={`${title} · engine outputs, ABC × XYZ classification and NASA POWER weather`} testid={tid ? `${tid}-origin` : undefined} />
      </div>
      <div>{drivers.map(d => <DriverRow key={d.id} line={d} centered={centered} />)}</div>
    </div>
  );
}

export function PageState({ error, forbidden, onRetry }) {
  if (forbidden) {
    return (
      <div className="tile p-6 text-sm" data-testid="fc-forbidden">
        <div className="font-medium">Not available for your role</div>
        <div className="text-muted mt-1">{error}</div>
        <Link to="/forecast/overview" data-testid="fc-forbidden-back" className="mt-3 inline-flex items-center h-8 px-3 border border-border rounded-sm text-[12.5px] hover:border-primary hover:text-primary">Back to the Forecast overview</Link>
      </div>
    );
  }
  if (error) {
    return (
      <div className="tile p-6 text-sm" data-testid="fc-error">
        <div className="text-critical font-medium">Couldn't load this page.</div>
        <div className="text-muted mt-1">{error}</div>
        <button data-testid="fc-retry" onClick={onRetry} className="mt-3 h-8 px-3 border border-border rounded-sm text-[12.5px] hover:border-primary hover:text-primary">Try again</button>
      </div>
    );
  }
  return <div className="tile p-6 text-sm text-muted animate-pulse" data-testid="fc-loading">Loading the latest model outputs…</div>;
}
